import React from 'react';
import styled from 'styled-components';
import Breadcrumbs from '../components/Breadcrumbs';

const PageContainer = styled.div`
  padding-top: 80px;
  padding-bottom: 4rem;
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 2rem;
  text-align: center;
`;

const Section = styled.section`
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
`;

const Text = styled.p`
  color: #64748b;
  line-height: 1.7;
  margin-bottom: 1rem;
  font-size: 1.1rem;
`;

const List = styled.ul`
  list-style-type: disc;
  margin-left: 1.5rem;
  margin-bottom: 1rem;
  color: #64748b;
  
  li {
    margin-bottom: 0.5rem;
    line-height: 1.7;
    font-size: 1.1rem;
  }
`;

function Privacy() {
  return (
    <PageContainer>
      <Breadcrumbs items={[{ label: 'Privacy Policy', path: '/privacy' }]} />
      <ContentWrapper>
        <Title>Privacy Policy</Title>
        
        <Section>
          <SectionTitle>1. Information We Collect</SectionTitle>
          <Text>
            We collect information that you provide directly to us, including:
          </Text>
          <List>
            <li>Name and contact information</li>
            <li>Shopify store URL and related data</li>
            <li>Payment information</li>
            <li>Communication preferences</li>
            <li>Technical data about your store's performance</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>2. How We Use Your Information</SectionTitle>
          <Text>
            We use the collected information for the following purposes:
          </Text>
          <List>
            <li>Providing and improving our testing services</li>
            <li>Processing your transactions</li>
            <li>Sending you technical notifications and updates</li>
            <li>Analyzing and optimizing our services</li>
            <li>Protecting against fraudulent or illegal activity</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>3. Information Sharing</SectionTitle>
          <Text>
            We do not sell your personal information. We may share your information with:
          </Text>
          <List>
            <li>Service providers who assist in our operations</li>
            <li>Professional advisors and auditors</li>
            <li>Law enforcement when required by law</li>
            <li>Business partners with your consent</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>4. Data Security</SectionTitle>
          <Text>
            We implement appropriate technical and organizational security measures to
            protect your information. However, no method of transmission over the Internet
            is 100% secure, and we cannot guarantee absolute security.
          </Text>
        </Section>

        <Section>
          <SectionTitle>5. Your Rights</SectionTitle>
          <Text>
            You have the right to:
          </Text>
          <List>
            <li>Access your personal information</li>
            <li>Correct inaccurate information</li>
            <li>Request deletion of your information</li>
            <li>Opt-out of marketing communications</li>
            <li>Withdraw consent where applicable</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>6. Cookies and Tracking</SectionTitle>
          <Text>
            We use cookies and similar tracking technologies to collect information about
            your browsing behavior and preferences. You can control cookie settings through
            your browser preferences.
          </Text>
        </Section>

        <Section>
          <SectionTitle>7. Children's Privacy</SectionTitle>
          <Text>
            Our services are not intended for children under 13 years of age. We do not
            knowingly collect or maintain information from children under 13.
          </Text>
        </Section>

        <Section>
          <SectionTitle>8. Changes to Privacy Policy</SectionTitle>
          <Text>
            We may update this Privacy Policy from time to time. We will notify you of any
            material changes by posting the new Privacy Policy on this page and updating
            the effective date.
          </Text>
        </Section>

        <Section>
          <SectionTitle>9. International Data Transfers</SectionTitle>
          <Text>
            Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws. When we transfer your data internationally, we implement appropriate safeguards:
          </Text>
          <List>
            <li>Standard contractual clauses approved by regulatory authorities</li>
            <li>Data processing agreements with service providers</li>
            <li>Compliance with EU-US and Swiss-US Privacy Shield frameworks</li>
            <li>Server locations in privacy-law compliant jurisdictions</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>10. Specific Data Processing Activities</SectionTitle>
          <Text>
            We process your data for the following specific purposes:
          </Text>
          <List>
            <li>Store performance analysis and optimization</li>
            <li>Security vulnerability detection and prevention</li>
            <li>Customer support and communication</li>
            <li>Service improvement and feature development</li>
            <li>Compliance with legal and regulatory requirements</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>11. Data Protection Rights</SectionTitle>
          <Text>
            Under applicable data protection laws, you have the following rights:
          </Text>
          <List>
            <li>Right to access - Obtain a copy of your personal data</li>
            <li>Right to rectification - Correct inaccurate or incomplete data</li>
            <li>Right to erasure - Request deletion of your personal data</li>
            <li>Right to restrict processing - Limit how we use your data</li>
            <li>Right to data portability - Receive your data in a structured format</li>
            <li>Right to object - Oppose processing of your personal data</li>
          </List>
          <Text>
            To exercise these rights, please contact our Data Protection Officer at dpo@revguard.com.
          </Text>
        </Section>

        <Section>
          <SectionTitle>12. Retention Periods</SectionTitle>
          <Text>
            We retain different types of data for specific periods:
          </Text>
          <List>
            <li>Account information: Duration of account activity plus 2 years</li>
            <li>Testing data: 12 months from collection</li>
            <li>Communication records: 3 years from last contact</li>
            <li>Payment information: 7 years (legal requirement)</li>
            <li>Technical logs: 90 days</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>13. Contact Us</SectionTitle>
          <Text>
            If you have any questions about this Privacy Policy or our data practices, please contact us:
          </Text>
          <List>
            <li>Email: privacy@revguard.com</li>
            <li>Data Protection Officer: dpo@revguard.com</li>
            <li>Postal Address: [Company Address]</li>
            <li>Phone: [Phone Number]</li>
          </List>
        </Section>
      </ContentWrapper>
    </PageContainer>
  );
}

export default Privacy;